import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CameraImageViewer } from '../../../components/CameraImageViewer';
import { ComplianceLayerLegend } from '../../../components/ComplianceLayerLegend';
import { ImageItemDetailsDialog } from '../../../components/ImageItemDetailsDialog';
import { ModFilters } from '../../../components/ModFilters';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { useEnvironment } from '../../../hooks/useEnvironment';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import {
  configItemsToRecord,
  usePlanogramComplianceConfig,
} from '../../../hooks/usePlanogramComplianceConfig';
import { useProductRecognition } from '../../../hooks/useProductRecognition';
import { ApiService } from '../../../services/api';
import { GtApiService } from '../../../services/gtApi';
import {
  BBox,
  BoxAction,
  CameraDetails,
  CameraImageTimelineResponse,
  PlanogramComplianceResponse,
  PogBBox,
  ProductRecognitionBBox,
  ProductRecognitionMask,
  ProductRecognitionPolygon,
} from '../../../types';
import { getPlanogramComplianceActions } from '../../../utils/planogramComplianceActions';
import { ActionsPanel } from './ActionsPanel';
import {
  ACTION_LAYER_CONFIG,
  COMPLIANCE_COLOR,
  PlanogramComplianceControlCenter,
} from './PlanogramComplianceControlCenter';
import { LAYER_CONFIG, ProductRecognitionControlCenter } from './ProductRecognitionControlCenter';

type SelectedCameraViewProps = {
  camera: CameraDetails;
  storeName: string;
  cameraTimeline?: CameraImageTimelineResponse | null;
  chainId?: number;
  storeId?: number;
};

export const SelectedCameraView: React.FC<SelectedCameraViewProps> = ({
  camera,
  storeName,
  cameraTimeline,
  chainId,
  storeId,
}) => {
  const [selectedIlItemIds, setSelectedIlItemIds] = useState<Set<number>>(new Set());
  const [selectionType, setSelectionType] = useState<'pog' | 'realogram'>('pog');
  const [originalClickedItemId, setOriginalClickedItemId] = useState<number | undefined>(undefined);
  const [boxActions, setBoxActions] = useState<BoxAction[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [enabledMods, setEnabledMods] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<
    'triggers' | 'product-recognition' | 'template' | 'planogram-compliance'
  >('template');
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [isRunningPlanogramModel, setIsRunningPlanogramModel] = useState(false);
  const [enabledProductRecognitionLayers, setEnabledProductRecognitionLayers] = useState<
    Set<string>
  >(() => new Set(LAYER_CONFIG.filter((l) => l.enabledByDefault).map((l) => l.key)));
  const [enabledPlanogramComplianceLayers, setEnabledPlanogramComplianceLayers] = useState<
    Set<string>
  >(() => new Set(ACTION_LAYER_CONFIG.map((l) => l.key)));
  const [selectedComplianceLayers, setSelectedComplianceLayers] = useState<Set<string>>(
    () => new Set(Object.keys(COMPLIANCE_COLOR))
  );
  const [selectedDetectionItem, setSelectedDetectionItem] = useState<any>(null);
  const [selectedDetectionColor, setSelectedDetectionColor] = useState<string | undefined>(
    undefined
  );

  const [showDetectionDialog, setShowDetectionDialog] = useState(false);
  const [isSavingInference, setIsSavingInference] = useState(false);
  const [savedGtSampleId, setSavedGtSampleId] = useState<string | null>(null);
  const [showPogs, setShowPogs] = useState(true);
  const [showAllBoxes, setShowAllBoxes] = useState(true);
  const [showProductImages, setShowProductImages] = useState(false);
  const [isPlanogramDataFromCache, setIsPlanogramDataFromCache] = useState(false);
  const [isV2, setIsV2] = useState(false);

  // Track if we've already attempted to auto-run the model for the current image
  const autoRunAttemptedRef = useRef<boolean>(false);

  const { token, legacyToken } = useAuthContext();
  const { isProd } = useEnvironment();
  const navigate = useNavigate();

  // Use the product recognition hook
  const {
    productRecognitionData,
    isRunningModel: isRunningProductRecognition,
    isDataFromCache: isProductRecognitionFromCache,
    runProductRecognitionModel,
    loadFromCache: loadProductRecognitionFromCache,
    clearCache: clearProductRecognitionCache,
  } = useProductRecognition();

  // Use the image preloader hook
  const { preloadedImages, preloadedImagesB64, isLoadingFirstImage } = useImagePreloader({
    cameraTimeline,
    authToken: legacyToken,
    pogImageUri: camera.pogImageUri,
  });

  // Get current image and POG data first
  const currentImage =
    cameraTimeline?.images?.[cameraTimeline.images.length - 1 - selectedImageIndex];

  // Fetch planogram compliance configs from API only when tab is active
  const {
    config: planogramComplianceConfig,
    defaultConfig: defaultPlanogramComplianceConfig,
    updateConfig: setPlanogramComplianceConfig,
    resetConfig: resetPlanogramComplianceConfig,
  } = usePlanogramComplianceConfig(
    activeTab === 'planogram-compliance' ? currentImage?.imageId : null
  );

  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const handleBoxClick = useCallback((bbox: BBox) => {
    // TODO: Fix me - this should be a TemplateBBox
    setBoxActions((prevBoxActions) => {
      const existingAction = prevBoxActions.find((action) => action.bboxId === bbox.bboxId);
      if (!existingAction) {
        return [
          ...prevBoxActions,
          {
            bboxId: bbox.bboxId,
            tagName: bbox.tagName,
            productId: bbox.productId,
            upc: bbox.upc,
          },
        ];
      }
      return prevBoxActions;
    });
  }, []);

  const updateBoxAction = (bboxId: number, updates: Partial<BoxAction>) => {
    setBoxActions((prev) =>
      prev.map((action) => (action.bboxId === bboxId ? { ...action, ...updates } : action))
    );
  };

  const removeBoxAction = (bboxId: number) => {
    setBoxActions((prev) => prev.filter((action) => action.bboxId !== bboxId));
  };

  const clearAllActions = () => {
    setBoxActions([]);
  };

  const sendActions = async () => {
    if (boxActions.length === 0) {
      showErrorToast('Please select a store, group, and add actions before sending.');
      return;
    }
    try {
      toggleLoading(true);
      await ApiService.sendActions(
        token,
        storeName,
        camera.deviceName,
        currentImage?.imageId,
        boxActions
      );
      setBoxActions([]);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error sending actions');
    } finally {
      toggleLoading(false);
    }
  };

  const runCamera = async () => {
    try {
      toggleLoading(true);

      // If user is on the latest image (selectedImageIndex === cameraTimeline.images.length - 1),
      // send undefined/null image_id, otherwise send the current image's image_id
      const isOnLatestImage = selectedImageIndex === (cameraTimeline?.images?.length || 1) - 1;
      const imageIdToSend = isOnLatestImage ? undefined : currentImage?.imageId;

      await ApiService.runCamera(token, camera.deviceName, imageIdToSend);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error running camera');
    } finally {
      toggleLoading(false);
    }
  };

  const handleDetectionClick = useCallback((detection: any) => {
    setSelectedDetectionItem(detection.item);
    setSelectedDetectionColor(detection.color);
    setShowDetectionDialog(true);
  }, []);

  const handleActionClick = useCallback(
    (detection: any) => {
      // For realogram clicks, use rogItemId to select that specific item
      const rogItemId = detection?.item?.rogItemId ?? detection?.rogItemId;
      if (rogItemId === undefined || rogItemId === null) return;

      // Toggle logic:
      if (selectedIlItemIds.has(rogItemId)) {
        setSelectedIlItemIds(new Set()); // Deselect if already selected
        setOriginalClickedItemId(undefined);
      } else {
        // Realogram click: Select by rogItemId, and also add pogItemId for planogram highlighting
        const pogItemId = detection?.item?.pogItemId ?? detection?.pogItemId;
        const idsToSelect = new Set([rogItemId]);
        if (pogItemId !== undefined && pogItemId !== null) {
          idsToSelect.add(pogItemId);
        }
        setSelectedIlItemIds(idsToSelect);
        setOriginalClickedItemId(rogItemId);
        setSelectionType('realogram'); // This is a realogram box click
      }
    },
    [selectedIlItemIds]
  );

  const handlePogBoxClick = (pogBox: any) => {
    if (activeTab === 'planogram-compliance') {
      const ilItemId = pogBox?.ilItemId;
      if (ilItemId === undefined || ilItemId === null) return;

      if (selectedIlItemIds.has(ilItemId)) {
        setSelectedIlItemIds(new Set()); // Deselect
        setOriginalClickedItemId(undefined);
      } else {
        // Planogram click: Only highlight the clicked planogram item
        // The realogram highlighting and action filtering will be handled by the action filtering logic
        setSelectedIlItemIds(new Set([ilItemId])); // Only the clicked item for planogram highlighting
        setOriginalClickedItemId(ilItemId);
        setSelectionType('pog'); // This is a POG click
      }
      return;
    }
    setSelectedDetectionItem(pogBox);
    setSelectedDetectionColor('#3b82f6');
    setShowDetectionDialog(true);
  };

  const handlePogBoxRightClick = useCallback((pogBox: PogBBox) => {
    setSelectedDetectionItem(pogBox);
    setShowDetectionDialog(true);
    setSelectedDetectionColor('#3b82f6');
  }, []);

  const handleTemplateBoxClick = useCallback((templateBox: any) => {
    setSelectedDetectionItem(templateBox.item);
    setSelectedDetectionColor(templateBox.color);
    setShowDetectionDialog(true);
  }, []);

  const closeDetectionDialog = useCallback(() => {
    setShowDetectionDialog(false);
    setSelectedDetectionItem(null);
    setSelectedDetectionColor(undefined);
  }, []);

  useEscapeKey(closeDetectionDialog);

  const clearModelCache = () => {
    // Clear product recognition cache
    clearProductRecognitionCache();

    // Clear planogram compliance cache
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('planogram-compliance-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));

    // Clear planogram compliance data
    setPlanogramComplianceData(null);
    setIsPlanogramDataFromCache(false);

    showSuccessToast('Cache cleared successfully');
  };

  // Wrapper to call runProductRecognitionModel with currentImage
  const handleRunProductRecognition = useCallback(
    (options: { useApiCache?: boolean; triggerPogCompliance?: boolean } = {}) => {
      runProductRecognitionModel(currentImage?.imageId, options);
    },
    [currentImage?.imageId, runProductRecognitionModel]
  );

  const handleSaveInference = useCallback(async () => {
    if (!productRecognitionData?.inferenceId) {
      showErrorToast('No inference data to save');
      return;
    }
    try {
      setIsSavingInference(true);
      const result = await GtApiService.createGtSampleFromInference(token, {
        inference_id: productRecognitionData.inferenceId,
      });
      setSavedGtSampleId(result.gt_sample_id);
      showSuccessToast(`Inference saved (ID: ${result.gt_sample_id})`);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error saving inference');
    } finally {
      setIsSavingInference(false);
    }
  }, [productRecognitionData?.inferenceId, token, showErrorToast, showSuccessToast]);

  useEffect(() => {
    setSavedGtSampleId(null);
  }, [productRecognitionData?.inferenceId]);

  const runPlanogramComplianceModel = async () => {
    if (!currentImage) {
      showErrorToast('No image selected');
      return;
    }
    if (!planogramComplianceConfig) {
      showErrorToast('Config not loaded yet');
      return;
    }
    // Check session storage cache first
    const cacheKey = `planogram-compliance-${
      currentImage.imageId
    }-${isV2}-${JSON.stringify(planogramComplianceConfig)}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        // Check if cache is less than 30 minutes old
        const cacheTime = parsed.timestamp || 0;
        const now = Date.now();
        const thirtyMinutes = 30 * 60 * 1000;

        if (now - cacheTime < thirtyMinutes) {
          setPlanogramComplianceData(parsed.data);
          setIsPlanogramDataFromCache(true);
          return;
        }
      } catch (e) {
        sessionStorage.removeItem(cacheKey);
      }
    }

    try {
      setIsRunningPlanogramModel(true);

      const result = await ApiService.runRayPlanogramCompliance(
        token,
        currentImage.imageId,
        isV2,
        configItemsToRecord(planogramComplianceConfig)
      );

      // Cache the result
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: result,
          timestamp: Date.now(),
        })
      );

      setPlanogramComplianceData(result);
      setIsPlanogramDataFromCache(false);
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : 'Error running planogram compliance program'
      );
    } finally {
      setIsRunningPlanogramModel(false);
    }
  };

  // This takes the compliance action status and maps to the planogram item using il_item_id.
  // Later, I use the combination of il_item_id and status to assign the correct color to the planogram
  // item.
  const planogramStatusMap = useMemo(() => {
    const map = new Map<number, string>();
    if (planogramComplianceData && planogramComplianceData.boxes) {
      for (const box of planogramComplianceData.boxes) {
        let status = box.complianceStatus;
        map.set(box.pogItemId, status);
      }
    }
    return map;
  }, [planogramComplianceData]);

  const isFilteringByComplianceLayer = useMemo(
    () => selectedComplianceLayers.size !== Object.keys(COMPLIANCE_COLOR).length,
    [selectedComplianceLayers]
  );

  // Reset image index when camera changes - start with latest image
  useEffect(() => {
    if (cameraTimeline?.images && cameraTimeline.images.length > 0) {
      setSelectedImageIndex(cameraTimeline.images.length - 1);
    } else {
      setSelectedImageIndex(0);
    }
  }, [camera.deviceName, cameraTimeline?.images]);

  // Load cached product recognition data when image changes
  useEffect(() => {
    if (!currentImage) return;

    // Only load cache if on product-recognition tab
    if (activeTab !== 'product-recognition') return;

    loadProductRecognitionFromCache(currentImage.imageId);
  }, [currentImage?.imageId, activeTab, currentImage, loadProductRecognitionFromCache]);

  // Run product recognition model when switching to product-recognition tab if no data exists
  useEffect(() => {
    if (activeTab !== 'product-recognition') {
      autoRunAttemptedRef.current = false;
    } else if (!productRecognitionData && !isRunningProductRecognition && currentImage) {
      // Check if we've already attempted to auto-run
      if (!autoRunAttemptedRef.current) {
        autoRunAttemptedRef.current = true;

        handleRunProductRecognition({ useApiCache: true });
      }
    }
  }, [
    activeTab,
    currentImage,
    productRecognitionData,
    isRunningProductRecognition,
    handleRunProductRecognition,
  ]);

  const currentTemplate = currentImage
    ? cameraTimeline?.templates?.[currentImage.templateId]
    : null;

  // Get all unique tag names from camera mods and ALL POGs (not just filtered ones)
  const allTagNames = useMemo(() => {
    return camera.mods.map((mod) => mod.tagName);
  }, [camera.mods]);

  // Get POG data for the device and filter by enabled mods
  const currentPogs = useMemo(() => {
    const allPogs = cameraTimeline?.pogs ? Object.values(cameraTimeline.pogs) : [];

    // Filter POGs to only show those with enabled tag names
    const filteredByMod = allPogs
      .filter(
        (pog) =>
          pog?.tagName && (enabledMods.has(pog.tagName) || !allTagNames.includes(pog.tagName))
      )
      .sort((a, b) => {
        if (!a || !b) return 0;
        return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
      });

    // If on planogram-compliance tab, filter POG items by selected compliance layers
    if (activeTab === 'planogram-compliance') {
      return filteredByMod.map((pog) => {
        const filteredBboxes = pog.bboxes.filter((bbox) => {
          if (!bbox.ilItemId || !planogramStatusMap.has(bbox.ilItemId)) {
            return true;
          }

          // If all layers are selected, don't filter
          if (!isFilteringByComplianceLayer) {
            return true;
          }

          const status = planogramStatusMap.get(bbox.ilItemId);
          return status && selectedComplianceLayers.has(status);
        });

        return {
          ...pog,
          bboxes: filteredBboxes,
        };
      });
    }

    return filteredByMod;
  }, [
    cameraTimeline,
    enabledMods,
    allTagNames,
    activeTab,
    selectedComplianceLayers,
    planogramStatusMap,
    isFilteringByComplianceLayer,
  ]);

  useEffect(() => {
    setEnabledMods(new Set(allTagNames));
  }, [allTagNames]);

  const toggleMod = (tagName: string) => {
    setEnabledMods((prev) => {
      const newMods = new Set(prev);
      if (newMods.has(tagName)) {
        newMods.delete(tagName);
      } else {
        newMods.add(tagName);
      }
      return newMods;
    });
  };

  const toggleComplianceLayer = (layer: string) => {
    setSelectedComplianceLayers((prev) => {
      const newLayers = new Set(prev);
      if (newLayers.has(layer)) {
        newLayers.delete(layer);
      } else {
        newLayers.add(layer);
      }
      return newLayers;
    });

    // Reset selections when toggling compliance layers
    setSelectedIlItemIds(new Set());
    setOriginalClickedItemId(undefined);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!cameraTimeline?.images || cameraTimeline.images.length <= 1) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedImageIndex((prev) => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedImageIndex((prev) => Math.min(cameraTimeline.images.length - 1, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [cameraTimeline?.images]);

  // Filter bboxes based on enabled mods
  const filteredBboxes = useMemo(() => {
    if (!currentTemplate?.bboxes) return [];
    return currentTemplate.bboxes.filter((bbox) => {
      // Find which mod this bbox belongs to by checking tag_name
      return enabledMods.has(bbox.tagName);
    });
  }, [currentTemplate?.bboxes, enabledMods]);

  const preloadedImage = currentImage ? preloadedImagesB64[currentImage.imageUri] : undefined;

  // Helper function to get product recognition detections for overlay
  const productRecognitionDetections = useMemo(() => {
    if (!productRecognitionData || activeTab !== 'product-recognition') return [];

    const detections: any[] = [];

    // Add detections from each enabled layer using LAYER_CONFIG
    LAYER_CONFIG.forEach((layerConfig) => {
      if (enabledProductRecognitionLayers.has(layerConfig.key)) {
        const layerData = productRecognitionData[layerConfig.key];
        if (Array.isArray(layerData)) {
          layerData.forEach(
            (item: ProductRecognitionBBox | ProductRecognitionPolygon | ProductRecognitionMask) => {
              if (item.shape && Array.isArray(item.shape) && item.shape.length === 4) {
                // Check if this is a polygon item
                const isPolygon =
                  'points' in item && Array.isArray(item.points) && item.points.length > 0;

                let image = null;
                if (showProductImages) {
                  image =
                    item.vizSimCandidates?.find((candidate: any) => candidate.isConfident)
                      ?.productImageUri || null;
                }

                // Check for PNG mask data (e.g. visual obstructions)
                const maskImage =
                  'pngData' in item ? (item as ProductRecognitionMask).pngData.b64 : undefined;

                detections.push({
                  shape: item.shape,
                  color: layerConfig.bboxColor,
                  strokeWidth: 10,
                  item: item, // Include the original item data for dialog
                  isPolygon: isPolygon,
                  points: isPolygon ? item.points : undefined,
                  image,
                  maskImage,
                });
              }
            }
          );
        }
      }
    });

    return detections;
  }, [productRecognitionData, activeTab, enabledProductRecognitionLayers, showProductImages]);

  // Helper function to get planogram compliance actions for overlay
  const planogramComplianceActions = useMemo(() => {
    if (activeTab !== 'planogram-compliance' || !showAllBoxes) return [];

    const allActions = getPlanogramComplianceActions(
      planogramComplianceData,
      enabledPlanogramComplianceLayers,
      ACTION_LAYER_CONFIG,
      selectedIlItemIds,
      selectionType,
      originalClickedItemId
    );

    // If all layers are selected, don't filter
    if (!isFilteringByComplianceLayer) {
      return allActions;
    }

    // Filter actions by selected compliance layers
    return allActions.filter((action) => {
      const item = action.item as any;
      if (!item?.pogItemId) return true; // Keep items without pogItemId

      const status = planogramStatusMap.get(item.pogItemId);
      return status && selectedComplianceLayers.has(status);
    });
  }, [
    planogramComplianceData,
    activeTab,
    enabledPlanogramComplianceLayers,
    selectedIlItemIds,
    selectionType,
    showAllBoxes,
    originalClickedItemId,
    planogramStatusMap,
    selectedComplianceLayers,
    isFilteringByComplianceLayer,
  ]);

  // Helper function to get template detections for overlay
  const templateDetections = useMemo(() => {
    if (activeTab !== 'template') return [];

    const detections: any[] = [];

    // Add template boxes from camera template data (filteredBboxes)
    filteredBboxes.forEach((bbox) => {
      detections.push({
        shape: [
          Math.min(bbox.x1, bbox.x2),
          Math.min(bbox.y1, bbox.y2),
          Math.abs(bbox.x2 - bbox.x1),
          Math.abs(bbox.y2 - bbox.y1),
        ] as [number, number, number, number],
        color: '#10b981', // Green color for template boxes
        strokeWidth: 10,
        item: bbox, // Include the original bbox data for dialog
        image: showProductImages && bbox.imageUri ? bbox.imageUri : undefined,
      });
    });

    return detections;
  }, [filteredBboxes, activeTab, showProductImages]);

  // Computed values based on activeTab for CameraImageViewer
  const cameraImageProps = useMemo(() => {
    return {
      filteredBboxes: activeTab === 'triggers' && showAllBoxes ? filteredBboxes : [],
      onBoxClick: activeTab === 'triggers' ? handleBoxClick : () => {},
      boxActions: activeTab === 'triggers' ? boxActions : [],
      productRecognitionDetections: showAllBoxes
        ? activeTab === 'product-recognition'
          ? productRecognitionDetections
          : activeTab === 'template'
            ? templateDetections
            : []
        : [],
      onProductRecognitionClick:
        activeTab === 'product-recognition'
          ? handleDetectionClick
          : activeTab === 'template'
            ? handleTemplateBoxClick
            : undefined,
      planogramComplianceActions: showAllBoxes
        ? activeTab === 'planogram-compliance'
          ? planogramComplianceActions
          : activeTab === 'template'
            ? templateDetections
            : []
        : [],
      onPlanogramComplianceClick:
        activeTab === 'planogram-compliance'
          ? handleActionClick
          : activeTab === 'template'
            ? handleTemplateBoxClick
            : undefined,
    };
  }, [
    activeTab,
    showAllBoxes,
    filteredBboxes,
    boxActions,
    productRecognitionDetections,
    templateDetections,
    planogramComplianceActions,
    handleBoxClick,
    handleDetectionClick,
    handleTemplateBoxClick,
    handleActionClick,
  ]);

  return (
    <div className="space-y-6" key={camera.deviceName}>
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Combined Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Toggle Controls - Icons Only */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPogs(!showPogs)}
                className={`p-2 rounded-md transition-colors ${
                  showPogs
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                }`}
                title="Toggle POG visibility"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setShowAllBoxes(!showAllBoxes)}
                className={`p-2 rounded-md transition-colors ${
                  showAllBoxes
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                }`}
                title="Toggle all boxes display"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </button>
              <button
                onClick={() => setShowProductImages(!showProductImages)}
                className={`p-2 rounded-md transition-colors ${
                  showProductImages
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                }`}
                title="Toggle product images on boxes"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2m-5 7h2m-2 4h2"
                  />
                  <circle cx="12" cy="10" r="2" />
                </svg>
              </button>

              {/* Data Collection Mode Button */}
              {currentImage && chainId && storeId && (
                <a
                  title="Data Collection mode"
                  className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  href={`/embeddings/candidates/${currentImage.imageId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/embeddings/candidates/${currentImage.imageId}`);
                  }}
                >
                  DC
                </a>
              )}

              {currentImage && (
                <a
                  title="Outs Correction"
                  className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  href={`/outs-selection/${currentImage.imageId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/outs-selection/${currentImage.imageId}`);
                  }}
                >
                  OC
                </a>
              )}

              {/* AI Audit Button */}
              {currentImage && chainId && storeId && (
                <a
                  title="AI Audit Preview Mode"
                  className="p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                  href={`/images/${currentImage.imageId}/audit?${new URLSearchParams({ store_name: storeName, chain_id: chainId.toString(), store_id: storeId.toString(), cameraId: camera.cameraId.toString() }).toString()}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const params = new URLSearchParams();
                    params.set('store_name', storeName);
                    params.set('chain_id', chainId.toString());
                    params.set('store_id', storeId.toString());
                    params.set('cameraId', camera.cameraId.toString());
                    navigate(`/images/${currentImage.imageId}/audit?${params.toString()}`);
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </a>
              )}

              {/* Copy URL with image_id Button */}
              {currentImage?.imageId && (
                <button
                  title="Copy URL with image_id"
                  className="p-2 bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 rounded-lg transition-colors"
                  onClick={async () => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('image_id', currentImage.imageId.toString());

                    try {
                      await navigator.clipboard.writeText(url.toString());
                      showSuccessToast('URL to current image copied!');
                    } catch (err) {
                      showErrorToast('Failed to copy URL');
                    }
                  }}
                >
                  <ContentCopy fontSize="small" />
                </button>
              )}
            </div>

            {/* Separator */}
            <div className="h-6 w-px bg-gray-300"></div>

            {/* Mod Filter Buttons */}
            <ModFilters
              allTagNames={allTagNames}
              enabledMods={enabledMods}
              toggleMod={toggleMod}
              planogramComplianceData={planogramComplianceData}
            />
          </div>
          <p className="text-sm text-gray-500">
            {selectedImageIndex + 1} of {cameraTimeline?.images.length ?? 1}
          </p>
        </div>

        <div className="space-y-4 mb-4">
          {/* Slider */}
          <input
            type="range"
            min="0"
            max={(cameraTimeline?.images.length ?? 1) - 1}
            value={selectedImageIndex}
            onChange={(e) => setSelectedImageIndex(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />

          {/* Navigation buttons */}
          <div className="flex justify-center space-x-4 mb-4 items-center">
            <div className="text-sm text-gray-600">
              {currentImage && new Date(currentImage.detectedAt).toLocaleString()}
            </div>
          </div>
        </div>

        {isLoadingFirstImage || !preloadedImage ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <p className="text-gray-500">Loading camera images...</p>
            </div>
          </div>
        ) : (
          <div>
            <CameraImageViewer
              preloadedImage={preloadedImage}
              filteredBboxes={cameraImageProps.filteredBboxes}
              boxActions={cameraImageProps.boxActions}
              productRecognitionDetections={cameraImageProps.productRecognitionDetections}
              planogramComplianceActions={cameraImageProps.planogramComplianceActions}
              showProductImages={showProductImages}
              onBoxClick={cameraImageProps.onBoxClick}
              onProductRecognitionClick={cameraImageProps.onProductRecognitionClick}
              onPlanogramComplianceClick={cameraImageProps.onPlanogramComplianceClick}
              showPogs={showPogs}
              currentPogs={currentPogs}
              preloadedImagesB64={preloadedImagesB64}
              selectedIlItemIds={selectedIlItemIds}
              planogramStatusMap={planogramStatusMap}
              onPogBoxClick={handlePogBoxClick}
              onPogBoxRightClick={handlePogBoxRightClick}
            />
            <div className="flex gap-4 items-center mt-4 w-full">
              <div className="flex flex-col gap-4 w-full">
                {/* Actions Legend - under camera image */}
                {activeTab === 'planogram-compliance' && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-800">Actions:</span>
                    {ACTION_LAYER_CONFIG.map((layer) => (
                      <div key={layer.key} className="flex items-center gap-1">
                        <span
                          className={`inline-block w-4 h-4 rounded ${layer.color} border border-gray-400`}
                        />
                        <span className="text-xs text-gray-700">{layer.label}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Current image info */}
                {currentImage && (
                  <div className="text-xs text-gray-500">
                    Image ID: {currentImage.imageId} | Template ID: {currentImage.templateId} |
                    Bounding boxes: {filteredBboxes.length}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-4 w-full">
                {/* Compliance Legend - under POG images */}
                {activeTab === 'planogram-compliance' && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-800">Compliance:</span>
                    <ComplianceLayerLegend
                      selectedComplianceLayers={selectedComplianceLayers}
                      planogramStatusMap={planogramStatusMap}
                      onToggleComplianceLayer={toggleComplianceLayer}
                    />
                  </div>
                )}
                <div className="flex">
                  <div className="ml-auto bg-black/70 text-white text-xs rounded px-2 py-1 backdrop-blur-sm flex flex-col items-end gap-1 min-w-[300px]">
                    <span>🖱️ Scroll to zoom • Drag to pan • Double-click to reset</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('template')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'template'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Template
            </button>
            <button
              onClick={() => setActiveTab('product-recognition')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'product-recognition'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Product Recognition
            </button>
            <button
              onClick={() => setActiveTab('planogram-compliance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'planogram-compliance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Planogram Compliance
            </button>
            <button
              onClick={() => setActiveTab('triggers')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'triggers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              disabled={isProd}
            >
              Triggers
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'triggers' && !isProd && (
            <ActionsPanel
              mods={camera.mods}
              actions={boxActions}
              bboxes={filteredBboxes}
              onUpdateAction={updateBoxAction}
              onRemoveAction={removeBoxAction}
              onClearAll={clearAllActions}
              onSend={sendActions}
              onRunCamera={runCamera}
            />
          )}

          {activeTab === 'product-recognition' && (
            <ProductRecognitionControlCenter
              onRunModel={handleRunProductRecognition}
              onSaveInference={handleSaveInference}
              savedGtSampleId={savedGtSampleId}
              productRecognitionData={productRecognitionData}
              isLoading={isRunningProductRecognition}
              isSavingInference={isSavingInference}
              currentImageId={currentImage?.imageId}
              currentImageElement={
                currentImage ? preloadedImages[currentImage.imageUri] : undefined
              }
              onEnabledLayersChange={setEnabledProductRecognitionLayers}
              chainId={chainId}
              storeId={storeId}
              isDataFromCache={isProductRecognitionFromCache}
              onClearCache={clearModelCache}
              enabledLayers={enabledProductRecognitionLayers}
            />
          )}

          {activeTab === 'planogram-compliance' && !planogramComplianceConfig && (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500 mr-2" />
              Loading config...
            </div>
          )}

          {activeTab === 'planogram-compliance' && planogramComplianceConfig && (
            <PlanogramComplianceControlCenter
              onRunModel={runPlanogramComplianceModel}
              planogramComplianceData={planogramComplianceData}
              isLoading={isRunningPlanogramModel}
              currentImageId={currentImage?.imageId}
              currentImageElement={
                currentImage ? preloadedImages[currentImage.imageUri] : undefined
              }
              onEnabledLayersChange={setEnabledPlanogramComplianceLayers}
              enabledLayers={enabledPlanogramComplianceLayers}
              selectedIlItemIds={selectedIlItemIds}
              isDataFromCache={isPlanogramDataFromCache}
              onClearCache={clearModelCache}
              isV2={isV2}
              onIsV2Change={setIsV2}
              config={planogramComplianceConfig}
              defaultConfig={defaultPlanogramComplianceConfig}
              onConfigChange={setPlanogramComplianceConfig}
              onConfigReset={resetPlanogramComplianceConfig}
            />
          )}

          {activeTab === 'template' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Template Boxes</h3>
                <div className="text-sm text-gray-500">
                  Camera Template: {currentTemplate ? `ID ${currentTemplate.templateId}` : 'None'}
                </div>
              </div>
              {filteredBboxes.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Showing {filteredBboxes.length} template box(es) from camera template
                  </p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>Click on template boxes in the image to view product details</p>
                    <p>Toggle mod filters above to show/hide specific template boxes</p>
                  </div>
                </div>
              ) : currentTemplate?.bboxes ? (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No template boxes visible. Enable mod filters above to show template boxes.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    No camera template available for this image.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detection Details Dialog */}
      {selectedDetectionItem && (
        <ImageItemDetailsDialog
          item={selectedDetectionItem}
          cameraImageElement={currentImage ? preloadedImages[currentImage.imageUri] : undefined}
          isOpen={showDetectionDialog}
          onClose={closeDetectionDialog}
          layerBboxColor={selectedDetectionColor}
          chainId={chainId}
          storeId={storeId}
          storeName={storeName}
          shouldShowEmbeddings={true}
          cartonBoxes={productRecognitionData?.cartonBoxes}
        />
      )}
    </div>
  );
};
